import {Component, Input, OnChanges, SimpleChanges, ViewChild} from "@angular/core";
import {UntypedFormArray, UntypedFormBuilder, Validators} from "@angular/forms";
import {Message} from "@bbwt/classes";
import {CreateMode, GridComponent, IGridColumn, IGridSettings, ITableSettings, UpdateMode} from "@features/grid";
import {PatientService} from "@main/app-layout/components/patient-switch/patient.service";
import {UserHelperService} from "@main/users/user-helper.service";
import {IEpisode} from "@project/models/episodes/episode";
import {EpisodeStatus} from "@project/models/episodes/episode-status";
import {IEpisode_Diagnosis} from "@project/models/episodes/episode_diagnosis";
import {Diagnosis} from "@project/diagnosis/diagnosis";
import * as moment from "moment";
import {MessageService} from "primeng/api";
import {EpisodeService} from "./episode.service";
import {IRavenIssue} from "@main/raven/models/raven-issue";
import {Permissions} from "@main/permissions/permissions";
import {RavenControlComponent} from "@main/raven/raven-control.component";
import {setCurrentTime} from "@bbwt/utils/date-utils";
import {EpisodeOfflineStorage} from "./episode-offline.storage";
import {AppOnlineStateService} from "@main/pwa/app-online-state.service";
import {
    isGenericPrescriptionEpisode,
    isNoteDraftEpisode,
    isRoutineTreatmentEpisode,
    isSelfDirectedGymRecoveryEpisode
} from "@project/episode/episode-select/episode-defaults";
import {EpisodeCreatedFrom} from "@project/models/episodes/episode_createdFrom";

@Component({ template: "" })
export abstract class EpisodeBaseComponent implements OnChanges {
    showDuplicateEpisodesDialog: boolean = false;
    savingEpisode = false;
    searchingDuplEpisodes = false;
    initializingEpisode = true;

    episodeForm = this.fb.group({
        id: ["0"],
        private: [false],
        alwaysActive: [false],
        significant: [false],
        createDateTime: [new Date()],
        diagnoses: this.fb.array([], [Validators.minLength(1), Validators.required]),
        assignedClinicianUserId: [null, Validators.required]
    });

    privateEpisodesAllowed = false;
    alwaysActiveAllowed = false;
    significantAllowed = false;
    showDuplEpisodes = false;
    duplEpisodes: IEpisode[] = [];
    duplBodyPartSide: IEpisode[] = [];
    tempEpisodeDupCheak: IEpisode = null;
    navigateBackOnDuplicateEditCancel: boolean = true;

    isDraft = false;
    originalPrivate = false;

    originalDiagnoses: Map<string, string> = new Map();
    originalCreateDateTime: Date;
    changes: number = 0;

    ravenIssue: IRavenIssue = null;

    ravenModuleEnabled: boolean = false;
    ravenValidConfig: boolean = true;

    autoSaveIsOn: boolean = false;

    isNoteDraftEpisode: boolean = false;

    @ViewChild(RavenControlComponent) ravenControl: RavenControlComponent;

    diagnosisTableSettings = <ITableSettings>{
        columns: <IGridColumn[]>[
            {
                field: "name",
                header: "Diagnosis",
                sortable: false
            },

            {
                field: "code",
                header: "Code",
                sortable: false
            },
            {
                field: "bodyPartName",
                header: "Body Part",
                sortable: false
            },

            {
                field: "medicalSystemName",
                header: "Medical System",
                sortable: false,
                displayHandler: (val: any) => val ?? "N/A"
            },
            {
                field: "bodySideName",
                header: "Body Side",
                sortable: false
            }
        ],
        paginator: false
    };

    async restoreEpisodes(episode_: IEpisode) {
        this.patchEpisode(episode_);
        const { duplEpisodes, duplBodyPartSide, episode } = await this.searchDuplicateDiagnoses(episode_);
        if (duplEpisodes.length || duplBodyPartSide.length) {
            this.duplEpisodesFound({
                duplEpisodes,
                duplBodyPartSide,
                tempEpisodeDupCheak: episode,
                navigateBackOnDuplicateEditCancel: true
            });
            return { duplEpisodeDialog: true };
        }
    }

    diagnosisGridSettings = <IGridSettings>{
        deleteFunc: (diagnosis: Diagnosis, index: number) => {
            this.diagnosesArray.removeAt(index);
            this.diagnosesGrid.setTableProperty("value", this.diagnosesArray.value);
            if (this.originalDiagnoses.get(diagnosis.code) === diagnosis.bodySideName) {
                this.changes++;
            } else {
                this.changes--;
            }
            this.onItemDeleted(index, diagnosis);
        },
        deletingOfflineEnabled: true,
        createMode: CreateMode.Disabled,
        updateMode: UpdateMode.Disabled,
        actionsColumnWidth: "50px",
        hideSummary: true
    };

    onItemDeleted(index: number, diagnosis: Diagnosis): void {}

    @Input() episodeId: string;
    @Input() draftedEpisode: IEpisode = null;
    @Input() patientId = this.patientService.currentPatientId;
    @Input() isWalkInDraft = false;
    @Input() isPrescription: boolean;

    @ViewChild(GridComponent) diagnosesGrid: GridComponent;

    createFrom: EpisodeCreatedFrom;

    subEntitiesMinDate: Date;

    constructor(
        protected episodeService: EpisodeService,
        protected messageService: MessageService,
        private fb: UntypedFormBuilder,
        protected patientService: PatientService,
        protected userHelperService: UserHelperService,
        protected appOnlineStateService: AppOnlineStateService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.draftedEpisode?.currentValue || changes?.episodeId) {
            this.initEpisode();
        }
    }

    abstract viewDuplEpisode(episodeId: string);

    get diagnosesArray() {
        return this.episodeForm.get("diagnoses") as UntypedFormArray;
    }

    get assignedClinicianUserId() {
        return this.episodeForm.get("assignedClinicianUserId");
    }

    get createDateTime() {
        return this.episodeForm.get("createDateTime");
    }

    async confirmCreateEpisode() {
        if (this.episodeId != null && this.episodeId != "0") {
            return this.updateEpisode();
        } else {
            return this.createEpisode(this.isPrescription);
        }
    }

    addDiagnosis(diagnosis: Diagnosis) {
        if (!diagnosis) {
            return;
        }

        var currentDiagnoses = <Diagnosis[]>this.diagnosesArray.value;

        const isDuplicate = currentDiagnoses.some(
            d =>
                (diagnosis.code != null && diagnosis.code === d.code) ||
                (diagnosis.code == null &&
                    d.name === diagnosis.name &&
                    d.bodyPartName === diagnosis.bodyPartName &&
                    d.medicalSystemName === diagnosis.medicalSystemName &&
                    d.bodySideName === diagnosis.bodySideName)
        );

        if (isDuplicate) {
            if (diagnosis.code) {
                this.messageService.add(Message.Error(`Diagnosis with code "${diagnosis.code}" already exists`));
            } else {
                this.messageService.add(
                    Message.Error(
                        `Diagnosis with name "${diagnosis.name}", body part "${diagnosis.bodyPartName}", medical system "${diagnosis.medicalSystemName}", and body side "${diagnosis.bodySideName}" already exists`
                    )
                );
            }
        } else {
            this.diagnosesArray.push(this.fb.control(diagnosis));

            this.diagnosesGrid.setTableProperty("value", this.diagnosesArray.value);

            if (this.originalDiagnoses.get(diagnosis.code) !== diagnosis.bodySideName) {
                this.changes++;
            } else {
                this.changes--;
            }
        }
    }

    addRavenIssue(currentRavenIssue: IRavenIssue) {
        if (!currentRavenIssue) {
            return;
        }

        this.ravenIssue = currentRavenIssue;
    }

    clearDiagnosis() {
        this.diagnosesArray.clear();
        this.diagnosesGrid.setTableProperty("value", []);
    }

    protected async updateEpisode(): Promise<IEpisode> {
        this.savingEpisode = true;

        try {
            const oldEpisode = this.createEpisodeObj();
            const newEpisode = await this.episodeService.updateEpisodeDiagnoses(oldEpisode);

            await this.updateEpisodeDraftStatus(newEpisode);
            newEpisode.createDateTime = new Date(newEpisode.createDateTime);

            this.patchEpisode(newEpisode);

            oldEpisode.createDateTime = this.originalCreateDateTime; // Just for raven check;
            if(newEpisode.state == EpisodeStatus.Active && !this.autoSaveIsOn){
                await this.episodeService.checkDateUpdateForRaven(oldEpisode, newEpisode);
            }

            this.originalCreateDateTime = new Date(newEpisode.createDateTime);
            return newEpisode;
        } finally {
            this.savingEpisode = false;
        }
    }

    async save(isDraft: boolean = false, draftedEpisode: IEpisode = null, autoSaveIsOn: boolean = false) {
        if (this.episodeForm.invalid) {
            if (this.diagnosesArray?.value.length == 0 && !(isDraft && autoSaveIsOn)) {
                this.messageService.add(Message.Error("At least one diagnosis must be added"));
            }
            return;
        }

        const isNewEpisode =
            this.episodeId === null ||
            this.episodeId === undefined ||
            this.episodeId === "0" ||
            (draftedEpisode !== null && this.isWalkInDraft);
        const isLocalEpisode = EpisodeOfflineStorage.get(this.episodeId);

        if (isNewEpisode || (this.appOnlineStateService.isAppOnline && isLocalEpisode)) {
            if (!this.appOnlineStateService.isAppOnline || isDraft || draftedEpisode !== null) {
                return this.createEpisode(isDraft, false, false);
            }
            if (this.appOnlineStateService.isAppOnline && isLocalEpisode) {
                return this.createEpisode(isNewEpisode, false, false);
            }
            const { duplEpisodes, duplBodyPartSide, episode } = await this.searchDuplicateDiagnoses(null);
            if (duplEpisodes.length || duplBodyPartSide.length) {
                this.duplEpisodesFound({
                    duplEpisodes,
                    duplBodyPartSide,
                    tempEpisodeDupCheak: episode,
                    navigateBackOnDuplicateEditCancel: true
                });
                return { duplEpisodeDialog: true };
            } else {
                return this.createEpisode(isNewEpisode, false, false);
            }
        } else {
            const episode = await this.episodeService.get(draftedEpisode !== null ? draftedEpisode.id : this.episodeId);
            if (episode.ravenIssueId) {
                this.ravenIssue.id = episode.ravenIssueId;
                this.ravenIssue.referenceNumber = episode.ravenIssue.referenceNumber;
            }
            if (!isDraft && episode.state === EpisodeStatus.Draft) {
                const { duplEpisodes, duplBodyPartSide } = await this.searchDuplicateDiagnoses();
                if (duplEpisodes.length || duplBodyPartSide.length) {
                    this.duplEpisodesFound({
                        duplEpisodes,
                        duplBodyPartSide,
                        tempEpisodeDupCheak: episode,
                        navigateBackOnDuplicateEditCancel: true
                    });
                    return { duplEpisodeDialog: true };
                }
            }

            return await this.updateEpisode();
        }
    }

    selectExistingDuplEpisode(episodeId: string) {
        const selectedEpisode = this.duplEpisodes?.find(x => x.id == episodeId);
        return selectedEpisode ?? this.duplBodyPartSide?.find(x => x.id == episodeId);
    }

    async initEpisode() {
        this.initializingEpisode = true;
        try {
            let episode: IEpisode = this.draftedEpisode;
            if (this.episodeId && this.episodeId != "0" && this.draftedEpisode == null) {
                episode = await this.episodeService.get(this.episodeId);
                episode.createDateTime = new Date(episode.createDateTime);
                this.ravenIssue = episode.ravenIssue;
            }

            this.patchEpisode(episode);

            this.isDraft = episode?.state == EpisodeStatus.Draft;

            this.originalPrivate = episode ? episode.private : false;

            this.privateEpisodesAllowed =
                (this.userHelperService.hasPermission(Permissions.ConsultationHistoryWritePrivateEpisodesUnrestricted) ||
                    (this.userHelperService.hasPermission(Permissions.ConsultationHistoryPrivateEpisodeWrite) &&
                        (!this.episodeId ||
                            this.episodeId === "0" ||
                            this.userHelperService.userId === episode?.assignedClinicianUserId ||
                            this.userHelperService.userId === episode?.userId))) &&
                !episode?.isMasterEpisode;

            this.significantAllowed =
                this.userHelperService.hasPermission(Permissions.ConsultationHistoryWritePrivateEpisodesUnrestricted) ||
                this.userHelperService.hasPermission(Permissions.ConsultationHistoryPrivateEpisodeWrite) ||
                this.userHelperService.userId === episode?.assignedClinicianUserId;

            this.alwaysActiveAllowed =
                !isGenericPrescriptionEpisode(episode) &&
                !isRoutineTreatmentEpisode(episode) &&
                !isSelfDirectedGymRecoveryEpisode(episode) &&
                (this.userHelperService.hasPermission(Permissions.ConsultationHistoryWritePrivateEpisodesUnrestricted) ||
                    this.userHelperService.hasPermission(Permissions.ConsultationHistoryPrivateEpisodeWrite) ||
                    this.userHelperService.userId === episode?.assignedClinicianUserId);

            // Disabling flags when editing an episode does not work (partially corrected) #298952
            this.setFormCheckboxControlsState();

            if (this.ravenModuleEnabled && this.ravenValidConfig) {
                if (episode?.ravenIssueId) {
                    this.ravenControl.writeValue(episode.ravenIssue);
                }
            }

            episode?.diagnoses.forEach(x => this.originalDiagnoses.set(x.diagnosisCode, x.bodySide));
            this.originalCreateDateTime = new Date(episode?.createDateTime);

            this.isNoteDraftEpisode = isNoteDraftEpisode(episode);
            if (this.isNoteDraftEpisode) {
                this.clearDiagnosis();
            }
        } finally {
            this.initializingEpisode = false;
        }
    }

    setFormCheckboxControlsState() {
        if (this.privateEpisodesAllowed) {
            this.episodeForm.get("private")?.enable();
        } else {
            this.episodeForm.get("private")?.disable();
        }

        if (this.significantAllowed) {
            this.episodeForm.get("significant")?.enable();
        } else {
            this.episodeForm.get("significant")?.disable();
        }

        if (this.alwaysActiveAllowed) {
            this.episodeForm.get("alwaysActive")?.enable();
        } else {
            this.episodeForm.get("alwaysActive")?.disable();
        }
    }

    protected async createEpisode(isDraft: boolean = false, saveAsLocalEpisode = false, setInactive: boolean = false): Promise<IEpisode> {
        this.savingEpisode = true;
        let episode = this.createEpisodeObj();
        episode.createFrom = this.createFrom;
        episode.state = isDraft ? (this.isPrescription ? EpisodeStatus.DraftPrescription : EpisodeStatus.Draft) : episode.state;

        if (setInactive) {
            episode.isInactive = true;
        }

        if (episode.createFrom == EpisodeCreatedFrom.Form) {
            episode.state = EpisodeStatus.FormDraft;
        }

        try {
            if (this.isWalkInDraft) {
                episode.state = EpisodeStatus.WalkInDraft;
            } else {
                episode.createDateTime = setCurrentTime(episode.createDateTime);
                episode = await this.episodeService.createEpisode(episode, saveAsLocalEpisode);
                this.patchEpisode(episode);
            }
            return episode;
        } finally {
            this.savingEpisode = false;
        }
    }

    private async searchDuplicateDiagnoses(episode_?: IEpisode) {
        const from = moment().subtract(12, "months");

        const episode = episode_ ? episode_ : this.createEpisodeObj();
        this.searchingDuplEpisodes = true;

        try {
            const duplEpisodes = await this.episodeService.getDuplicateEpisodes(episode, this.patientId);
            let duplBodyPartSide = await this.episodeService.getDuplicateBodypartEpisodes(episode, this.patientId);

            duplBodyPartSide = duplBodyPartSide.filter(x => !duplEpisodes.some(y => y.id === x.id) && moment(x.createDateTime) > from);

            return { duplEpisodes, duplBodyPartSide, episode };
        } finally {
            this.searchingDuplEpisodes = false;
        }
    }

    private patchEpisode(episode: IEpisode) {
        if (!episode) {
            episode = <IEpisode>{ id: "0", private: false, alwaysActive: false, diagnoses: [] };
        }

        this.episodeForm.patchValue(episode);
        this.diagnosesArray.clear();
        episode.diagnoses.forEach(ed =>
            this.diagnosesArray.push(
                this.fb.control(<Diagnosis>{
                    id: ed.diagnosisId,
                    code: ed.diagnosisCode,
                    name: ed.diagnosisName,
                    bodyPartName: ed.diagnosisBodyPartName,
                    medicalSystemName: ed.diagnosisMedicalSystemName,
                    bodySideName: ed.bodySide,
                    isOSIICSCode: ed.diagnosisIsOSIICSCode
                })
            )
        );
        this.episodeId = episode.id;
        this.diagnosesGrid?.setTableProperty("value", this.diagnosesArray.value);

        if (episode.id != "0") {
            const dates: Date[] = [
                ...(episode.notes?.length ? episode.notes?.map(n => n.creationDate) : []),
                ...(episode.documents?.length ? episode.documents?.map(d => d.uploadTime) : []),
                ...(episode.walkIns?.length ? episode.walkIns?.map(w => w.createDateTime) : [])
            ];
            this.subEntitiesMinDate = dates.length ? new Date(Math.min.apply(null, dates)) : null;
        }
    }

    private createEpisodeObj() {
        const episode: IEpisode = this.episodeForm.value;
        episode.diagnoses = (<Diagnosis[]>this.diagnosesArray.value).map(
            diagnosis =>
                <IEpisode_Diagnosis>{
                    id: "0",
                    episodeId: "0",
                    diagnosisId: diagnosis.id,
                    diagnosisName: diagnosis.name,
                    diagnosisCode: diagnosis.code,
                    diagnosisIsOSIICSCode: diagnosis.isOSIICSCode,
                    diagnosisBodyPartName: diagnosis.bodyPartName,
                    bodySide: diagnosis.bodySideName ? diagnosis.bodySideName : "",
                    diagnosisMedicalSystemName: diagnosis.medicalSystemName
                }
        );
        episode.patientUserId = this.patientId ?? this.patientService.currentPatientId;

        episode.ravenIssue = this.ravenIssue;
        if (this.ravenIssue && (this.ravenIssue.id !== "0" || !this.appOnlineStateService.isAppOnline)) {
            episode.ravenIssueId = this.ravenIssue.id;
        }
        return episode;
    }

    private duplEpisodesFound(obj: {
        duplEpisodes: IEpisode[];
        duplBodyPartSide: IEpisode[];
        tempEpisodeDupCheak: IEpisode;
        navigateBackOnDuplicateEditCancel: boolean;
    }) {
        this.showDuplEpisodes = true;
        this.duplEpisodes = obj.duplEpisodes ?? [];
        this.duplBodyPartSide = obj.duplBodyPartSide ?? [];
        this.tempEpisodeDupCheak = obj.tempEpisodeDupCheak;
        this.navigateBackOnDuplicateEditCancel = obj.navigateBackOnDuplicateEditCancel ?? true;
    }

    private async updateEpisodeDraftStatus(episode: IEpisode) {
        if(episode.state != EpisodeStatus.Draft || this.autoSaveIsOn || !this.appOnlineStateService.isAppOnline){
            return;
        }

        episode.state = EpisodeStatus.Active;
        await this.episodeService.updateEpisodeToActive(episode.id);
    }

    protected setAssignedClinicianId(id: string) {
        this.episodeForm.patchValue({ assignedClinicianUserId: id });
    }
}
